import { COLUMNS } from 'constants/filters';
import { WIDGET_CLIENTS_LIST, WIDGET_PROJECTS_HBAR } from 'constants/widgets';
import { defaultWidgetStatus } from 'factories/superreports';
import { mapValues, uniq } from 'lodash-es';
import { normalize } from 'normalizr';
import {
  publicReportListSchema,
  publicReportSchema,
  reportDataClientProjectSchema,
  reportDataSchema,
  reportListSchema,
  reportSchema,
} from 'schema/superreports';
import type { Hour } from 'types/hour';
import type { LightProject, Project } from 'types/project';
import type { GetAction } from 'types/redux';
import type { WidgetMetadata } from 'types/reports';
import { rewriteEmojiPath } from 'utils/emojis';
import { initialState } from './superreports.initialState';
import * as types from './superreports.types';
import type { TagNormalized, TagsDictionary } from './tags.types';

// CREATE_REPORT_DRAFT
export const createReportDraft = (
  state: types.State,
  action: GetAction<typeof types.createReportDraft>
): types.State => {
  return {
    ...state,
    draft: action.payload,
  };
};

// UPDATE_REPORT_DRAFT
export const updateReportDraft = (
  state: types.State,
  action: GetAction<typeof types.updateReportDraft>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      ...action.payload,
    },
  };
};

// CREATED_NEW_REPORT
export const createdNewReport = (
  state: types.State,
  action: GetAction<typeof types.createdNewReport>
): types.State => {
  const { entities, result } = normalize(action.payload, reportSchema);
  return {
    ...state,
    reportEntities: {
      ...state.reportEntities,
      ...entities.report,
    },
    reportResult: [...state.reportResult, result],
  };
};

// UPDATED_EXISTING_REPORT
export const updatedExistingReport = (
  state: types.State,
  action: GetAction<typeof types.updatedExistingReport>
): types.State => {
  const { entities } = normalize(action.payload, reportSchema);
  return {
    ...state,
    reportEntities: {
      ...state.reportEntities,
      ...entities.report,
    },
  };
};

// DELETE_REPORT
export const deleteReport = (
  state: types.State,
  action: GetAction<typeof types.deleteReport>
): types.State => {
  const reportResult = state.reportResult.filter(rId => rId !== action.payload);
  return {
    ...state,
    reportResult,
  };
};

// ADD_WIDGET_TO_DRAFT
export const addWidgetToDraft = (
  state: types.State,
  action: GetAction<typeof types.addWidgetToDraft>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: [...state.draft.widgets, action.payload],
    },
  };
};

// REMOVE_WIDGET_ON_DRAFT
export const removeWidgetOnDraft = (
  state: types.State,
  action: GetAction<typeof types.removeWidgetOnDraft>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets
        // Widget that don't exist on server can be directly removed
        .filter(widget => widget._ref !== action.payload)
        // Server-saved widgets get marked for deletion (executed as template draft is saved)
        .map(widget => (widget.id === action.payload ? { ...widget, _deleted: true } : widget)),
    },
  };
};

// EDIT_WIDGET_ON_DRAFT
export const editWidgetOnDraft = (
  state: types.State,
  action: GetAction<typeof types.editWidgetOnDraft>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        if (widget.id === action.payload.id || widget._ref === action.payload._ref) {
          return action.payload;
        }
        return widget;
      }),
    },
  };
};

// SET_QUERY
export const setQuery = (
  state: types.State,
  action: GetAction<typeof types.setQuery>
): types.State => {
  const newState = {
    ...state,
    query: {
      ...state.query,
      ...action.payload,
      filters: {
        ...state.query.filters,
        ...action.payload.filters,
      },
    },
  };

  if (state.query.filters && state.query.filters.resource === 'client') {
    newState.hourSummaryMaybeHasMore =
      (action.payload.page ?? 1) === 1 ? false : state.hourSummaryMaybeHasMore;
  } else {
    newState.timesheetMaybeHasMore =
      (action.payload.page ?? 1) === 1 ? false : state.timesheetMaybeHasMore;
  }

  if (action.payload.relativeDateRange) {
    newState.query.from = undefined;
    newState.query.to = undefined;
  } else if (action.payload.from) {
    newState.query.relativeDateRange = undefined;
  }
  return newState;
};

// FETCHED_PUBLIC_REPORT
export const fetchedPublicReports = (
  state: types.State,
  action: GetAction<typeof types.fetchedPublicReports>
): types.State => {
  const { entities, result } = normalize(action.payload, publicReportListSchema);

  return {
    ...state,
    publicReportEntities: entities?.publicReport ?? {},
    publicReportResult: result,
    publicReportMaybeHasMore: result.length >= types.PUBLIC_REPORTS_LIMIT,
  };
};

// CREATED_NEW_PUBLIC_REPORT
export const createdNewPublicReport = (
  state: types.State,
  action: GetAction<typeof types.createdNewPublicReport>
): types.State => {
  const { entities, result } = normalize(action.payload, publicReportSchema);
  if (!entities.publicReport) {
    return state;
  }
  return {
    ...state,
    publicReportEntities: {
      ...state.publicReportEntities,
      ...entities.publicReport,
    },
    publicReportResult: [...state.publicReportResult, result],
  };
};

// FETCHED_REPORT
export const fetchedReports = (
  state: types.State,
  action: GetAction<typeof types.fetchedReports>
) => {
  const { entities, result } = normalize(action.payload.data, reportListSchema);

  if (!entities) {
    return {
      ...state,
      reportEntities: initialState.reportEntities,
      reportResult: initialState.reportResult,
      reportsMaybeHasMore: false,
    };
  }
  return {
    ...state,
    reportEntities: { ...state.reportEntities, ...entities.report },
    reportResult: uniq([...state.reportResult, ...result]),
    reportsMaybeHasMore: action.payload.data.length === types.ALL_REPORTS_LIMIT,
  };
};

export const fetchedReport = (
  state: types.State,
  action: GetAction<typeof types.fetchedReport>
) => {
  return {
    ...state,
    reportEntities: { ...state.reportEntities, [action.payload.id]: action.payload },
    reportResult: uniq([...state.reportResult, action.payload.id]),
  };
};

// FETCHED_REPORT_DATA
export const fetchedReportData = (
  state: types.State,
  action: GetAction<typeof types.fetchedReportData>
): types.State => {
  const { data, reload } = action.payload;
  const { entities, result } = normalize(data, reportDataSchema);
  const { totals } = data;

  // Ugly hack for now, fix underlying issue - why doesn't result.projects return?
  const projectDataResult = result.clients.reduce((prev: Array<number>, current: number) => {
    if (!entities.client) {
      return prev;
    }
    const client = entities?.client[current];
    prev.push(...client.projects);
    return prev;
  }, []);

  if (entities.label) {
    entities.label = mapValues<TagsDictionary, TagNormalized>(entities.label, tag =>
      rewriteEmojiPath(tag)
    );
  }

  let draft: types.ReportDraft | undefined;

  if (!state.draft.hasLoadedInitialData) {
    (draft ??= { ...state.draft }).hasLoadedInitialData = true;
  }

  if (state.draft.multiCurrency !== data.multiple_currency_report) {
    (draft ??= { ...state.draft }).multiCurrency = data.multiple_currency_report;
  }

  const nextState = {
    ...state,
    draft: draft ?? state.draft,
    totals,
  };

  // If reloading data, replace the entities that are already there.
  // labelDataResult is replaced (not merged) so that stale labels from a previous
  // fetch are not shown after filters change (fixes TT-4828).
  // labelDataEntities is still merged so that entity metadata (name, emoji, etc.)
  // remains available for any expanded row lookups; the selector-level merge with
  // globalTagEntities already handles the TT-4326 case where tags on individual
  // entries must survive a reload.
  if (reload) {
    return {
      ...nextState,
      clientDataEntities: entities.client!,
      clientDataResult: result.clients,
      projectDataEntities: entities.project!,
      projectDataResult,
      labelDataEntities: { ...state.labelDataEntities, ...entities.label },
      labelDataResult: result.labels,
      userDataEntities: entities.user!,
      userDataResult: result.users,
      dayDataEntities: entities.day!,
      dayDataResult: result.days,
      teamDataEntities: entities.team!,
      teamDataResult: result.teams,
      linkedWorkDataEntities: entities.linked_work || {},
      linkedWorkDataResult: result.linked_work || [],
    };
  }

  // If not reloading data, shallow merge entities
  return {
    ...nextState,
    clientDataEntities: { ...state.clientDataEntities, ...entities.client },
    clientDataResult: uniq(state.clientDataResult.concat(result.clients)),
    projectDataEntities: { ...state.projectDataEntities, ...entities.project },
    projectDataResult: uniq(state.projectDataResult.concat(projectDataResult)),
    labelDataEntities: { ...state.labelDataEntities, ...entities.label },
    labelDataResult: uniq(state.labelDataResult.concat(result.labels)),
    userDataEntities: { ...state.userDataEntities, ...entities.user },
    userDataResult: uniq(state.userDataResult.concat(result.users)),
    dayDataEntities: { ...state.dayDataEntities, ...entities.day },
    dayDataResult: uniq(state.dayDataResult.concat(result.days)),
    teamDataEntities: { ...state.teamDataEntities, ...entities.team },
    teamDataResult: uniq(state.teamDataResult.concat(result.teams)),
    linkedWorkDataEntities: { ...state.linkedWorkDataEntities, ...(entities.linked_work || {}) },
    linkedWorkDataResult: uniq((state.linkedWorkDataResult || []).concat(result.linked_work || [])),
  };
};

// DELETE_PUBLIC_REPORT
export const deletePublicReport = (
  state: types.State,
  action: GetAction<typeof types.deletePublicReport>
): types.State => {
  const publicReportResult = state.publicReportResult.filter(rId => rId !== action.payload);
  return {
    ...state,
    publicReportResult,
  };
};

// INCREASE_SEQUENCE_STARTING_WITH
export const increaseSequenceStartingWith = (
  state: types.State,
  action: GetAction<typeof types.increaseSequenceStartingWith>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        if (widget.sequence >= action.payload && !widget._deleted) {
          return {
            ...widget,
            sequence: widget.sequence + 1,
          };
        }
        return widget;
      }),
    },
  };
};

// DECREASE_SEQUENCE_STARTING_WITH
export const decreaseSequenceStartingWith = (
  state: types.State,
  action: GetAction<typeof types.decreaseSequenceStartingWith>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        if (widget.sequence >= action.payload && !widget._deleted) {
          return {
            ...widget,
            sequence: widget.sequence - 1,
          };
        }
        return widget;
      }),
    },
  };
};

// UPDATE_WIDGET_COLUMNS
export const setWidgetColumns = (
  state: types.State,
  action: GetAction<typeof types.updateWidgetColumns>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const { sort_by, order, reference, names } = action.payload;
        const widgetMatches = reference && (reference === widget.id || reference === widget._ref);

        if (widgetMatches) {
          // Order of Th's matter;
          const orderedNames = COLUMNS.filter(cKey =>
            (names || widget.columns.names).includes(cKey)
          );
          return {
            ...widget,
            columns: {
              ...widget.columns,
              sort_by: sort_by || widget.columns.sort_by,
              order: order || widget.columns.order,
              names: orderedNames,
            },
          };
        }
        return widget;
      }),
    },
  };
};

// CHANGE_WIDGET_TYPE
// Change a widget to a specific type, including it's title.
// ie, from hours graph to cumulative hours grap
export const changeWidgetType = (
  state: types.State,
  action: GetAction<typeof types.changeWidgetType>
): types.State => {
  const { widgetType, widgetReference } = action.payload;
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const widgetMatches =
          widgetReference && (widgetReference === widget.id || widgetReference === widget._ref);

        if (!widgetMatches) return widget;

        return {
          ...widget,
          widget_type: widgetType,
        };
      }),
    },
  };
};

// COLLAPSE_ROW_ON_WIDGET
export const collapseRowOnWidget = (
  state: types.State,
  action: GetAction<typeof types.collapseRowOnWidget>
): types.State => {
  const { rowId, widgetReference } = action.payload;
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const widgetMatches =
          widgetReference && (widgetReference === widget.id || widgetReference === widget._ref);

        if (widgetMatches) {
          return {
            ...widget,
            _status: {
              ...widget._status,
              expandedRowIds: widget._status.expandedRowIds.filter(rId => rId !== rowId),
            },
          };
        }
        return widget;
      }),
    },
  };
};

// EXPAND_AND_LOAD_ROW_ON_WIDGET
export const expandRowOnWidget = (
  state: types.State,
  action: GetAction<typeof types.expandAndLoadRowOnWidget>
): types.State => {
  const { rowId, widgetReference } = action.payload;
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const widgetMatches =
          widgetReference && (widgetReference === widget.id || widgetReference === widget._ref);

        if (!widgetMatches) return widget;

        // Only mark a row as loading if we haven't already loaded all hours for row
        const hasLoadedAllRows = widget._status.loadedRowIds.includes(rowId);
        const loadingRowIds = hasLoadedAllRows
          ? widget._status.loadingRowIds
          : [...widget._status.loadingRowIds, rowId];

        return {
          ...widget,
          _status: {
            ...widget._status,
            expandedRowIds: [...widget._status.expandedRowIds, rowId],
            loadingRowIds,
          },
        };
      }),
    },
  };
};

// FETCHED_WIDGET_HOURS
export const fetchedWidgetHours = (
  state: types.State,
  action: GetAction<typeof types.fetchedWidgetHours>
): types.State => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const { rowId, widgetReference, hasMoreResults, hourIds } = action.payload;
        const widgetMatches =
          widgetReference && (widgetReference === widget.id || widgetReference === widget._ref);

        if (!widgetMatches) return widget;

        // Only mark a row as loading if we haven't already loaded all hours for row
        const loadedRowIds = hasMoreResults
          ? widget._status.loadedRowIds
          : [...widget._status.loadedRowIds, rowId];

        // Store hour IDs for this row (used by linked_work widget)
        const existingHourIds = widget._status.hourIdsByRowId?.[rowId] || [];
        const hourIdsByRowId = {
          ...widget._status.hourIdsByRowId,
          [rowId]: [...existingHourIds, ...hourIds],
        };

        return {
          ...widget,
          _status: {
            ...widget._status,
            loadingRowIds: widget._status.loadingRowIds.filter(rId => rId !== rowId),
            loadedRowIds,
            hourIdsByRowId,
          },
        };
      }),
    },
  };
};

export const fetchWidgetHoursError = (
  state: types.State,
  action: GetAction<typeof types.fetchWidgetHoursError>
) => {
  const { rowId, widgetReference } = action.payload;
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        const widgetMatches =
          widgetReference && (widgetReference === widget.id || widgetReference === widget._ref);

        if (widgetMatches) {
          return {
            ...widget,
            _status: {
              ...widget._status,
              expandedRowIds: widget._status.expandedRowIds.filter(rId => rId !== rowId),
              loadingRowIds: widget._status.loadingRowIds.filter(rId => rId !== rowId),
              loadedRowIds: widget._status.loadedRowIds.filter(rId => rId !== rowId),
            },
          };
        }
        return widget;
      }),
    },
  };
};

export const resetWidgetStatusOnDraft = (state: types.State) => {
  return {
    ...state,
    draft: {
      ...state.draft,
      widgets: state.draft.widgets.map(widget => {
        return {
          ...widget,
          _status: defaultWidgetStatus,
        };
      }),
    },
  };
};

export const updateWidgetOptions = (
  state: types.State,
  { payload }: ReturnType<typeof types.updateWidgetOptions>
): types.State => {
  if (!payload.reference) return state;

  const widgetIndex = state.draft.widgets.findIndex(
    w => w.id === payload.reference || w._ref === payload.reference
  );

  if (widgetIndex === -1) return state;

  const widgets = [...state.draft.widgets];
  const widget = { ...widgets[widgetIndex] };
  widget.options = {
    ...widget.options,
    ...payload.options,
  } as types.WidgetOptions;
  widgets.splice(widgetIndex, 1, widget);

  return {
    ...state,
    draft: {
      ...state.draft,
      widgets,
    },
  };
};

export const moveWidget = (
  state: types.State,
  { targetSequence, reference }: types.MoveWidgetParams
): types.State => {
  if (!reference) return state;

  const movedWidget = state.draft.widgets.find(w => w.id === reference || w._ref === reference);
  if (!movedWidget) return state;

  // If the widget is moved to the same or immediately following sequence, don't do anything
  if (targetSequence === movedWidget.sequence || targetSequence === movedWidget.sequence + 1)
    return state;

  const isMovingUp = targetSequence < movedWidget.sequence;

  // Because the drop target is always presented ahead of the widget it represents, we need to
  // decrement the target sequence to get the correct position when moving widgets further behind
  if (!isMovingUp) {
    targetSequence -= 1;
  }

  const widgets = state.draft.widgets.map(widget => {
    if (widget === movedWidget) {
      return {
        ...widget,
        sequence: targetSequence,
      };
    }

    if (isMovingUp) {
      // Push widgets in the affected range behind the moved widget
      if (widget.sequence >= targetSequence && widget.sequence < movedWidget.sequence) {
        return {
          ...widget,
          sequence: widget.sequence + 1,
        };
      }
    } else {
      // Pull widgets in the affected range ahead of the moved widget
      if (widget.sequence <= targetSequence && widget.sequence > movedWidget.sequence) {
        return {
          ...widget,
          sequence: widget.sequence - 1,
        };
      }
    }

    return widget;
  });

  return {
    ...state,
    draft: {
      ...state.draft,
      widgets,
    },
  };
};

export const fetchedWidgetMetadata = (
  state: types.State,
  payload: Array<WidgetMetadata>
): types.State => {
  // We've kinda reused WIDGET_CLIENTS_LIST for the projects-by-clients table, so we need to
  // override the title - should migrate this at some point though
  const titleOverrides: Record<string, string | undefined> = {
    [WIDGET_CLIENTS_LIST]: payload.find(item => item.widget_type === WIDGET_PROJECTS_HBAR)?.title,
  };

  const widgetMetadata = payload.reduce((temp, item) => {
    temp[item.widget_type] = {
      columns: item.columns.names,
      entity: item.group_by,
      title: titleOverrides[item.widget_type] ?? item.title,
    };
    return temp;
  }, {} as types.WidgetMetadataDictionary);

  return {
    ...state,
    widgetMetadata,
  };
};

/**
 * Synthesizes project entities from hours. We match the hour project to report projects to
 * determine whether the hour should be included in the report, but when we retrieve hours for
 * public reports we can already trust that the hours are included, so we synthesize project
 * entities from the hours to ensure they are displayed properly.
 *
 * @see `widgetRowHourListFactory` in src/factories/superreports/index.ts
 */
export const synthesizeProjectsFromHours = (
  state: types.State,
  hours: Array<Hour<Project | LightProject>>
): types.State => {
  const { entities, result } = normalize(hours, [
    {
      project: reportDataClientProjectSchema,
    },
  ]);

  // FIXME: Similar to fetchedReportData, the project IDs aren't mapped into an array by normalize
  const projectIds = result.map((project: any) => project.id);

  return {
    ...state,
    // Spread synthesized projects first to avoid overwriting already loaded projects
    projectDataEntities: { ...entities.project, ...state.projectDataEntities },
    projectDataResult: uniq(state.projectDataResult.concat(projectIds)),
  };
};
